<template>
  <div class="inlinechart-container">
    <template v-if="hasChart">
      <div class="table-container">
        <div
          id="hcps-table-header"
          class="table-header"
        >
          <p class="table-title">
            {{ chartData.title }}
          </p>
          <p class="table-subtitle">
            {{ chartData.subtitle }}
          </p>
        </div>
        <div class="chart">
          <table aria-labelledby="hcps-table-header">
            <thead>
              <tr>
                <th scope="col">
                  {{ `[${rowId}]` }}
                </th>
                <th
                  v-if="columnQuestion"
                  scope="col"
                >
                  {{ `[${columnId}]` }}
                </th>
                <th
                  scope="col"
                  class="numeric"
                >
                  {{ columnQuestion ? `Percentage within [${rowId}] category` : 'Percentage' }}
                </th>
                <th
                  scope="col"
                  class="numeric"
                  v-if="showCI()"
                >
                  Lower bound of 95% confidence interval for percentage
                </th>
                <th
                  scope="col"
                  class="numeric"
                  v-if="showCI()"
                >
                  Upper bound of 95% confidence interval for percentage
                </th>
                <th
                  scope="col"
                  class="numeric benchmark"
                  v-if="hasBenchmark()"
                >
                  CG-CAHPS benchmark for percentage <sup>{{ chartData.footnotes.benchmark.symbol }}</sup>
                </th>
                <th
                  scope="col"
                  class="numeric"
                >
                  Sample size
                </th>
                <th
                  scope="col"
                  class="numeric"
                >
                  Population size
                </th>
                <th
                  scope="col"
                  class="numeric"
                  v-if="showCI()"
                >
                  Lower bound of 95% confidence interval for population size
                </th>
                <th
                  scope="col"
                  class="numeric"
                  v-if="showCI()"
                >
                  Upper bound of 95% confidence interval for population size
                </th>
              </tr>
            </thead>
            <template v-if="columnQuestion">
              <tbody
                v-for="(r, ri) in tableData.rows"
                :key="r"
              >
                <tr
                  v-for="(c, ci) in tableData.columns"
                  :key="`${r}_${c}`"
                >
                  <template v-if="chartData.table && chartData.table[ri] && chartData.table[ri][ci]">
                    <th
                      v-if="ci === 0"
                      :rowspan="tableData.columns.length"
                      scope="rowgroup"
                    >
                      {{ r }}
                    </th>
                    <th scope="row">
                      {{ c }}
                    </th>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintProportion(chartData.table[ri][ci].proportion)) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                      v-if="showCI()"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintProportion(chartData.table[ri][ci].ciProportion.lower)) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                      v-if="showCI()"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintProportion(chartData.table[ri][ci].ciProportion.upper)) }}
                    </td>
                    <td
                      class="numeric"
                      v-if="hasBenchmark()"
                    >
                      {{ chartData.table[ri][ci].benchmark != null ? chartData.table[ri][ci].benchmark : 'n/a' }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintSampleSize(chartData.table[ri][ci].sampleSize)) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintPopulationSize(chartData.table[ri][ci].numerator)) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                      v-if="showCI()"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintPopulationSize(chartData.table[ri][ci].ciNumerator.lower)) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][ci].suppressed }"
                      v-if="showCI()"
                    >
                      {{ (chartData.table[ri][ci].suppressed ? suppressedSymbol : prettyPrintPopulationSize(chartData.table[ri][ci].ciNumerator.upper)) }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </template>
            <template v-else>
              <tbody>
                <tr
                  v-for="(r, ri) in tableData.rows"
                  :key="r"
                >
                  <template v-if="chartData.table[ri] && chartData.table[ri].length > 0">
                    <th scope="row">
                      {{ r }}
                    </th>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintProportion(chartData.table[ri][0].proportion) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                      v-if="showCI()"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintProportion(chartData.table[ri][0].ciProportion.lower) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                      v-if="showCI()"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintProportion(chartData.table[ri][0].ciProportion.upper) }}
                    </td>
                    <td
                      class="numeric"
                      v-if="hasBenchmark()"
                    >
                      {{ chartData.table[ri][0].benchmark != null ? chartData.table[ri][0].benchmark : 'n/a' }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintSampleSize(chartData.table[ri][0].sampleSize) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintPopulationSize(chartData.table[ri][0].numerator) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                      v-if="showCI()"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintPopulationSize(chartData.table[ri][0].ciNumerator.lower) }}
                    </td>
                    <td
                      class="numeric"
                      :class="{ suppressed: chartData.table[ri][0].suppressed }"
                      v-if="showCI()"
                    >
                      {{ chartData.table[ri][0].suppressed ? suppressedSymbol : prettyPrintPopulationSize(chartData.table[ri][0].ciNumerator.upper) }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </template>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  prettyPrintProportion,
  prettyPrintSampleSize,
  prettyPrintPopulationSize
} from '../utils.js'

export default {
  name: 'TableChart',
  data () {
    return {
      localChart: null
    }
  },
  props: {
    chart: {
      type: Object,
      default: null
    }
  },
  methods: {
    prettyPrintProportion,
    prettyPrintSampleSize,
    prettyPrintPopulationSize,
    showCI () {
      return this.getFilter('confidenceInterval')
    },
    hasBenchmark () {
      return this.chartData.footnotes.benchmark.hasBenchmark
    }
  },
  computed: {
    doesRowHaveSubpop () {
      return this.rowQuestion && this.rowQuestion.pop !== 'all patients'
    },
    rowPopulation () {
      return this.rowQuestion && this.rowQuestion.pop
    },
    doesColumnHaveSubpop () {
      return this.columnQuestion && this.columnQuestion.pop !== 'all patients'
    },
    columnPopulation () {
      return this.columnQuestion && this.columnQuestion.pop
    },
    doesFilterHaveSubpop () {
      return this.filterQuestion && this.filterQuestion.pop !== 'all patients'
    },
    filterPopulation () {
      return this.filterQuestion && this.filterQuestion.pop
    },
    anySubpops () {
      return this.doesRowHaveSubpop || this.doesColumnHaveSubpop || this.doesFilterHaveSubpop
    },
    hasChart () {
      return this.tableData != null
    },
    chartData () {
      return this.getChartData('tableChart')
    },
    tableData () {
      if (this.rowQuestion == null && this.columnQuestion == null) {
        return null
      }

      const chart = { rows: [], columns: [] }

      if (this.rowQuestion != null) {
        const rowLevels = this.filteredRowQuestion.levels
        chart.rows = rowLevels
          .map(l => l.label)
      } else {
        chart.rows = ['']
      }

      if (this.columnQuestion != null) {
        const levels = this.filteredColumnQuestion.levels
        chart.columns = levels
          .map(l => l.label)
      } else {
        chart.columns = ['']
      }

      return chart
    },
    suppressedSymbol () {
      return this.chartData.footnotes.suppressed ? this.chartData.footnotes.suppressed.symbol : ''
    },
    anySuppressed () {
      return this.chartData.table.some(row => {
        return row.some(cell => cell.suppressed)
      })
    },
    ...mapGetters(['rowId', 'columnId', 'filterId', 'rowQuestion', 'filteredRowQuestion', 'columnQuestion', 'filteredColumnQuestion', 'filterQuestion'])
  }
}
</script>

<style lang="scss" scoped>
.inlinechart-container {
    flex: 1 1 auto;

    .controls {
      display: flex;
      width: 100%;

      .row-selector, .col-selector {
        flex: 0 0 50%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .button-row {
          display: flex;
        }
      }
    }

    .table-header {
      text-align: center;
      font-family: Merriweather, serif;
      line-height: 1.2;
      color: #112e51;

      .table-title {
        font-size: 18px;
        margin-bottom: 16px;
     }

      .table-subtitle {
        font-size: 12px;
        margin-bottom: 24px;
      }
    }

    .chart {
      width: 100%;
      margin-bottom: 12px;
      overflow-x: auto;

      table {
        width: 100%;

        th, td {
          padding: 4px;
        }

        th {
          background-color: #112d4e;
          color: #fff;
          font-weight: bold;
          border: 1px solid #aaa;

          &.numeric {
            text-align: right;
          }

          &.benchmark sup {
            font-family: Roboto Mono Dagger, sans-serif;
          }
        }

        td {
          border: 1px solid #ddd;
          color: #333;

          &.numeric {
            text-align: right;
          }
        }

        td.suppressed {
          font-family: Merriweather, serif;
        }

        tbody {
          border-top: 2px solid #888;
          border-bottom: 2px solid #888;
        }

        tr:nth-child(even) {
          background-color: #f5f5f5;
        }
      }
    }
}
</style>
